import { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';

// Shadcn Components
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent } from "@/components/ui/card";

// Icons
import { ArrowLeft } from "lucide-react";

// Hooks
import { useTenantNavigation } from '../../hooks/navigation/useTenantNavigation';
import { useOrganizationJoin } from '../../hooks/authentication/useOrganizationJoin';

const OrganizationJoin = () => {
  const navigate = useNavigate();
  const { navigateWithTenant } = useTenantNavigation();
  const { formData, error, handleInputChange, handleSubmit } = useOrganizationJoin();

  const gradientStyle = {
    background: "linear-gradient(45deg, #f804b4 0%, #08ccc4 100%)"
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center px-4 py-12" 
      style={gradientStyle}
    >
      <div className="w-full max-w-lg bg-white rounded-lg shadow-2xl overflow-hidden">
        <div className="px-8 py-6">
          <div className="flex flex-col items-center">
            <img 
              src="/branding/TriEvents_Loading_Big_Text.gif" 
              alt="TriEvents Logo" 
              className="w-40 sm:w-48 h-auto mb-2"
            />
            <h1 className="text-2xl font-semibold text-foreground">Join Organization</h1>
            <p className="text-muted-foreground text-center text-sm mt-0.5 mb-4">
              Create an account to join this organization
            </p>
          </div>

          <div className="h-[300px] overflow-y-auto rounded-lg border bg-gray-50 scrollbar-thin scrollbar-track-muted/20 scrollbar-thumb-muted/40 hover:scrollbar-thumb-muted/60">
            <form onSubmit={handleSubmit} className="space-y-4 p-4">
              {error && (
                <Alert variant="destructive" className="mb-2">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              <Card>
                <CardContent className="pt-6 space-y-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="name">Name</Label>
                    <Input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('name', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      type="password"
                      id="password"
                      name="password"
                      placeholder="Enter your password"
                      value={formData.password}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('password', e.target.value)}
                      required
                    />
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="confirmPassword">Confirm Password</Label>
                    <Input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      placeholder="Confirm your password"
                      value={formData.confirmPassword}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('confirmPassword', e.target.value)}
                      required
                    />
                  </div>

                  {/* Password Requirements */}
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p className="font-medium">Password Requirements:</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>At least 8 characters long</li>
                      <li>At least one lowercase letter (a-z)</li>
                      <li>At least one uppercase letter (A-Z)</li>
                      <li>At least one number (0-9)</li>
                      <li>At least one special character (!@#$%^&*(),.?":{}|&lt;&gt;)</li>
                    </ul>
                  </div>

                  <div className="space-y-1.5">
                    <Label htmlFor="accessCode">Access Code</Label>
                    <Input
                      type="text"
                      id="accessCode"
                      name="accessCode"
                      placeholder="Enter organization access code"
                      value={formData.accessCode}
                      onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('accessCode', e.target.value)}
                      required
                    />
                  </div>
                </CardContent>
              </Card>
            </form>
          </div>

          <div className="space-y-3 mt-4">
            <Button 
              type="submit"
              size="lg"
              className="w-full transition-transform hover:scale-105"
              onClick={handleSubmit}
            >
              Join Organization
            </Button>
          </div>

          <div className="flex flex-col-reverse sm:flex-row items-center justify-between gap-4 pt-4 mt-4">
            <Button
              variant="outline"
              onClick={() => navigate('/auth')}
              className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
            <Button
              variant="outline"
              onClick={() => navigateWithTenant('/login')}
              className="transform hover:scale-105 transition-all duration-200 w-full sm:w-auto"
            >
              Login to Organization
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrganizationJoin; 